import { X } from 'lucide-react'
import type { Stage } from './fitThesisStages'

interface Block {
  num: string
  name: string
  question: string
  /** Narrative connector that introduces this block from the previous one. */
  connector?: string
}

interface StageDef {
  key: Stage
  label: string
  swatch: string
  ink: string
  blocks: Block[]
}

/**
 * Teaching panel for the 9-block fit thesis canvas. Each block carries the
 * key question Skerpa will ask, and the canvas's narrative connectors
 * (however / therefore / as a result / luckily) make the logic visible.
 *
 * Block names mirror the published "Market Fit Canvas" framework so users
 * can cross-reference outside material; internal block keys remain
 * persona/jtbd/pain/competitor/flaw/consequence/pitch/benefitA/benefitB.
 */
const STAGES: readonly StageDef[] = [
  {
    key: 'customer',
    label: 'Customer',
    swatch: 'var(--color-market-500)',
    ink: 'var(--color-market-700)',
    blocks: [
      { num: '01', name: 'Customer archetype', question: 'Who is likely to benefit, and what sets them apart?' },
      { num: '02', name: 'Job to be done',     question: 'What does the customer need to achieve?' },
      { num: '03', name: 'Root problem',       question: 'What causes them to look for a solution?', connector: 'however' },
    ],
  },
  {
    key: 'competitor',
    label: 'Competitors',
    swatch: 'var(--color-business-500)',
    ink: 'var(--color-business-700)',
    blocks: [
      { num: '04', name: 'Dominant competitor', question: "What solutions are available today?", connector: 'therefore' },
      { num: '05', name: 'Competitor flaws',    question: 'What are the drawbacks of dominant options?', connector: 'however' },
      { num: '06', name: 'Consequence',         question: "What goes wrong if the problem is never solved?", connector: 'as a result' },
    ],
  },
  {
    key: 'solution',
    label: 'Solution',
    swatch: 'var(--color-solution-700)',
    ink: 'var(--color-solution-800)',
    blocks: [
      { num: '07', name: 'Solution',           question: 'What do you offer to solve it?', connector: 'luckily' },
      { num: '08', name: 'Feature @ problem',  question: 'How does your solution solve the overarching problem?' },
      { num: '09', name: 'Feature @ competitor', question: 'How does your solution close the competitor flaw?' },
    ],
  },
] as const

export function FitThesisCanvasPeek({ onClose }: { onClose(): void }) {
  return (
    <section
      className="mb-7 rounded-lg border border-border bg-bg-card p-6 max-md:p-4"
      aria-labelledby="canvas-peek-title"
    >
      <header className="mb-5 flex items-baseline gap-3">
        <h2 id="canvas-peek-title" className="text-subhead font-semibold text-fg">
          The 9-block canvas
        </h2>
        <p className="text-caption text-fg-secondary">
          A thesis is a chain of nine answers, linked by{' '}
          <em className="not-italic font-medium text-fg">however</em>,{' '}
          <em className="not-italic font-medium text-fg">therefore</em>,{' '}
          <em className="not-italic font-medium text-fg">as a result</em>,{' '}
          <em className="not-italic font-medium text-fg">luckily</em>.
        </p>
        <button
          onClick={onClose}
          className="ml-auto inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-fg-muted transition hover:bg-bg-secondary hover:text-fg"
          aria-label="Close canvas overview"
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      <div className="grid grid-cols-[120px_1fr] gap-x-6 max-md:grid-cols-1 max-md:gap-y-1">
        {STAGES.map((stage, stageIdx) => (
          <StageRow key={stage.key} stage={stage} isFirst={stageIdx === 0} />
        ))}
      </div>
    </section>
  )
}

function StageRow({ stage, isFirst }: { stage: StageDef; isFirst: boolean }) {
  const borderClass = isFirst ? '' : 'border-t border-border'
  return (
    <>
      <div
        className={`flex items-center gap-2 py-3 ${borderClass} font-mono text-[11px] font-semibold uppercase tracking-wider max-md:border-t-0 max-md:pt-4`}
        style={{ color: stage.ink }}
      >
        <span className="h-2 w-2 rounded-sm" style={{ background: stage.swatch }} />
        {stage.label}
      </div>
      <div className={`flex flex-col gap-2 py-3 ${borderClass} max-md:border-t-0 max-md:py-1`}>
        {stage.blocks.map(block => (
          <div key={block.num} className="flex flex-col gap-1">
            {block.connector && (
              <span className="-mb-0.5 ml-7 font-mono text-[10px] font-semibold uppercase tracking-wider text-fg-muted">
                — {block.connector} —
              </span>
            )}
            <div className="grid grid-cols-[24px_180px_1fr] items-baseline gap-3 max-md:grid-cols-[24px_1fr] max-md:gap-2">
              <span className="font-mono text-[11px] font-semibold text-fg-muted">{block.num}</span>
              <span className="text-body font-medium text-fg">{block.name}</span>
              <span className="text-caption italic text-fg-secondary max-md:col-span-2 max-md:col-start-2">
                {block.question}
              </span>
            </div>
          </div>
        ))}
      </div>
    </>
  )
}
